import { useCallback, useEffect, useRef, useState } from "react";
import { useScribe } from "@elevenlabs/react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Loader2, Waves } from "lucide-react";
import { toast } from "sonner";

interface Props {
  onTranscriptTurn: (text: string) => Promise<void> | void;
  onPartial?: (text: string) => void;
  onActiveChange?: (active: boolean) => void;
  disabled?: boolean;
}

export function VoiceMicButton({ onTranscriptTurn, onPartial, onActiveChange, disabled = false }: Props) {
  const [connecting, setConnecting] = useState(false);
  const [processingTurn, setProcessingTurn] = useState(false);
  const latestPartialRef = useRef("");
  const lastQueuedTurnRef = useRef("");
  const pendingTurnsRef = useRef<string[]>([]);
  const flushingRef = useRef(false);

  const queueTurn = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (lastQueuedTurnRef.current === trimmed) return;
    lastQueuedTurnRef.current = trimmed;
    pendingTurnsRef.current.push(trimmed);
  }, []);

  const flushTurns = useCallback(async () => {
    if (flushingRef.current) return;
    flushingRef.current = true;
    setProcessingTurn(true);
    try {
      while (pendingTurnsRef.current.length > 0) {
        const nextTurn = pendingTurnsRef.current.shift();
        if (!nextTurn) continue;
        await onTranscriptTurn(nextTurn);
      }
    } finally {
      flushingRef.current = false;
      setProcessingTurn(false);
    }
  }, [onTranscriptTurn]);

  const scribe = useScribe({
    modelId: "scribe_v2_realtime",
    commitStrategy: "vad" as any,
    onPartialTranscript: (data: any) => {
      latestPartialRef.current = data?.text || "";
      onPartial?.(latestPartialRef.current);
    },
    onCommittedTranscript: (data: any) => {
      const text = (data?.text || "").trim();
      latestPartialRef.current = "";
      onPartial?.("");
      queueTurn(text);
      void flushTurns();
    },
  } as any);

  const start = useCallback(async () => {
    if (disabled) return;
    setConnecting(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const { data, error } = await supabase.functions.invoke("elevenlabs-scribe-token");
      if (error) throw error;
      if (!data?.token) throw new Error("No token returned");
      latestPartialRef.current = "";
      pendingTurnsRef.current = [];
      lastQueuedTurnRef.current = "";
      await scribe.connect({
        token: data.token,
        microphone: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      onActiveChange?.(true);
    } catch (e: any) {
      toast.error(e.message || "Mic / voice failed");
    } finally {
      setConnecting(false);
    }
  }, [disabled, onActiveChange, scribe]);

  const stop = useCallback(async () => {
    try {
      const commit = (scribe as any).commit;
      if (typeof commit === "function") {
        await commit();
      }
      await new Promise((r) => setTimeout(r, 250));
      if (latestPartialRef.current.trim()) {
        queueTurn(latestPartialRef.current);
      }
      await scribe.disconnect();
      latestPartialRef.current = "";
      onPartial?.("");
      await flushTurns();
      onActiveChange?.(false);
    } catch (e: any) {
      toast.error(e.message || "Could not stop mic");
    }
  }, [flushTurns, onActiveChange, onPartial, queueTurn, scribe]);

  useEffect(() => {
    return () => {
      try {
        const r: any = scribe.disconnect();
        if (r && typeof r.catch === "function") r.catch(() => {});
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = scribe.isConnected;

  return (
    <Button
      type="button"
      variant={active ? "default" : "outline"}
      className={`h-[60px] min-w-[160px] shrink-0 gap-2 px-4 ${active ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground" : ""}`}
      onClick={() => (active ? stop() : start())}
      disabled={connecting || disabled}
      title={active ? "Turn voice mode off" : "Turn voice mode on"}
    >
      {connecting ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : active ? (
        <MicOff className="h-4 w-4" />
      ) : (
        <Mic className="h-4 w-4" />
      )}
      <span className="text-xs font-medium">
        {connecting
          ? "Connecting..."
          : active
            ? processingTurn
              ? "Voice mode: sending"
              : "Voice mode: live"
            : "Start voice mode"}
      </span>
      {active && <Waves className="h-4 w-4 animate-pulse" />}
    </Button>
  );
}
